import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";

function formatDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}
function formatTime(iso) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export default function HistoryPage() {
  const toast = useToast();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState("");

  const fetchActivities = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/activities`, { withCredentials: true });
      setActivities(data || []);
    } catch (e) {
      toast.show({ variant: "error", title: "Failed to load", description: "Could not fetch activities." });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [toast]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const deleteActivity = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    try {
      await axios.delete(`${API}/activities/${id}`, { withCredentials: true });
      setActivities((prev) => prev.filter((a) => a.id !== id));
      toast.show({ variant: "success", title: "Deleted" });
    } catch {
      toast.show({ variant: "error", title: "Failed to delete" });
    }
  };

  const filtered = activities.filter((a) =>
    !query.trim() ||
    a.activity_description.toLowerCase().includes(query.toLowerCase()) ||
    a.date.includes(query)
  );

  // Group by date
  const grouped = filtered.reduce((acc, a) => {
    (acc[a.date] = acc[a.date] || []).push(a);
    return acc;
  }, {});
  const groupedKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div className="px-5 pt-6 max-w-2xl mx-auto" data-testid="history-page">
      <header className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">History</h1>
          <p className="text-slate-400 text-sm mt-1">{activities.length} {activities.length === 1 ? "entry" : "entries"} logged</p>
        </div>
        <button
          onClick={() => { setRefreshing(true); fetchActivities(); }}
          className="w-11 h-11 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-blue-400 hover:text-blue-300 btn-press"
          data-testid="refresh-history-btn"
          aria-label="Refresh"
        >
          <i className={`fas fa-arrows-rotate ${refreshing ? "spin" : ""}`}></i>
        </button>
      </header>

      <div className="bg-slate-800/60 border border-slate-700 rounded-2xl px-4 py-3 mb-5 flex items-center gap-3">
        <i className="fas fa-magnifying-glass text-slate-500"></i>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search activities or YYYY-MM-DD…"
          data-testid="history-search-input"
          className="bg-transparent w-full outline-none text-slate-100 placeholder-slate-500"
        />
        {query && (
          <button onClick={() => setQuery("")} className="text-slate-500 hover:text-slate-300" aria-label="Clear search">
            <i className="fas fa-xmark"></i>
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-10 h-10 border-4 border-blue-500/30 border-t-blue-500 rounded-full spin"></div>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState query={query} />
      ) : (
        <div className="space-y-6">
          {groupedKeys.map((dateKey, idx) => (
            <section key={dateKey} className="fade-in" style={{ animationDelay: `${idx * 60}ms` }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/15 text-blue-400 flex items-center justify-center">
                  <i className="fas fa-calendar"></i>
                </div>
                <h2 className="text-sm uppercase tracking-wider text-slate-300 font-semibold">{formatDate(dateKey)}</h2>
                <span className="text-xs text-slate-500 ml-auto">{grouped[dateKey].length}</span>
              </div>
              <div className="space-y-2">
                {grouped[dateKey].map((a) => (
                  <article
                    key={a.id}
                    data-testid="activity-card"
                    className="bg-slate-800/60 border border-slate-700 rounded-2xl p-4 hover:border-slate-600 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <p className="text-slate-100 leading-relaxed whitespace-pre-wrap">{a.activity_description}</p>
                        <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1.5">
                            <i className="fas fa-clock"></i>
                            {formatTime(a.created_at)}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <i className="fas fa-user"></i>
                            {a.user_name}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => deleteActivity(a.id)}
                        data-testid="delete-activity-btn"
                        className="w-9 h-9 rounded-lg text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 flex items-center justify-center"
                        aria-label="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ query }) {
  return (
    <div className="text-center py-16 fade-in">
      <div className="w-20 h-20 mx-auto rounded-2xl bg-slate-800 flex items-center justify-center mb-4">
        <i className="fas fa-inbox text-3xl text-slate-600"></i>
      </div>
      <h3 className="text-lg font-semibold text-slate-300">{query ? "No matches" : "No activities yet"}</h3>
      <p className="text-slate-500 text-sm mt-1">
        {query ? "Try a different search term." : "Start by recording your day on the Record tab."}
      </p>
    </div>
  );
}
